import { useLayoutEffect, useState, type RefObject } from "react";

const MAX_FLOW_HEIGHT = 100_000;

/** A document gets a new token when replaced, even though its WindowProxy survives. */
export function newEmbeddedFlowToken(): string {
  return crypto.randomUUID();
}

/** The sandbox measures itself; the host never reads an opaque document's DOM. */
export function embeddedSurfaceFlowMarkup(token: string): string {
  return `<style data-pa-embedded-flow>
html { height: auto !important; min-height: 0 !important; overflow-y: auto !important; }
body { height: auto !important; min-height: 0 !important; overflow: visible !important; }
</style><script data-pa-embedded-flow>
(function () {
  var token = ${JSON.stringify(token)};
  var previous = 0;
  function measure() {
    if (!document.body) return;
    var height = Math.ceil(Math.max(document.body.scrollHeight, document.body.getBoundingClientRect().height));
    if (!Number.isFinite(height) || height === previous) return;
    previous = height;
    parent.postMessage({ __pa: 1, method: 'surface.content_size', params: { token: token, height: height } }, '*');
  }
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(measure).observe(document.body);
  document.addEventListener('load', measure, true);
  if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  requestAnimationFrame(measure);
})();
</script>`;
}

/** Only this live embedded document may size its slot; other pages keep their own layout. */
export function useEmbeddedSurfaceFlow(
  frame: RefObject<HTMLIFrameElement | null>, token: string | undefined,
): number | undefined {
  const [reading, setReading] = useState<{ token: string; height: number } | null>(null);
  useLayoutEffect(() => {
    if (!token) return;
    const receive = (event: MessageEvent) => {
      const data = event.data;
      if (event.source !== frame.current?.contentWindow || data?.__pa !== 1
          || data.method !== "surface.content_size" || data.params?.token !== token) return;
      const height = data.params.height;
      if (typeof height !== "number" || !Number.isFinite(height) || height < 0) return;
      setReading({ token, height: Math.max(120, Math.min(MAX_FLOW_HEIGHT, Math.ceil(height))) });
    };
    window.addEventListener("message", receive);
    return () => window.removeEventListener("message", receive);
  }, [frame, token]);
  return token ? reading?.token === token ? reading.height : 420 : undefined;
}
